import { useState } from 'react';
import {
  Button,
  ChipToggle,
  Dialog,
  Field,
  Input,
  useToast,
} from '@/components/ui';
import { usePatientActions } from '@/features/patients/api';
import {
  CHECK_LABELS,
  REQUESTER_LABELS,
  TOPIC_LABELS,
  type DisclosureCheck,
  type DisclosureRequester,
  type DisclosureResult,
  type DisclosureTopic,
  type PatientSummary,
} from '@/features/patients/types';
import { isApiError } from '@/lib/api';
import { PatientPicker } from '@/pages/queue/PatientPicker';
import styles from './patients.module.css';

const keys = <T extends string>(r: Record<T, string>) => Object.keys(r) as T[];

/**
 * "Someone is asking about a patient" (Admin v2 #27): who is asking, about
 * whom, what they want and how identity was checked. Nothing is said until
 * the check passes; every answer is logged against the patient.
 */
export function DisclosureDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const a = usePatientActions();
  const toast = useToast();
  const [requester, setRequester] = useState<DisclosureRequester>('patient');
  const [patient, setPatient] = useState<PatientSummary | null>(null);
  const [topic, setTopic] = useState<DisclosureTopic>('appointment_time');
  const [check, setCheck] = useState<DisclosureCheck>('app_approved');
  const [name, setName] = useState('');
  const [result, setResult] = useState<DisclosureResult | null>(null);

  function close() {
    setResult(null);
    setPatient(null);
    setName('');
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Someone is asking about a patient"
      footer={
        result ? (
          <Button variant="primary" onClick={close}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!patient}
              loading={a.disclose.isPending}
              onClick={() =>
                patient &&
                a.disclose
                  .mutateAsync({
                    id: patient.id,
                    requester,
                    topic,
                    identityCheck: check,
                    requesterName: name.trim() || undefined,
                  })
                  .then(setResult)
                  .catch((err: unknown) =>
                    toast.show({
                      tone: 'error',
                      title: 'Not recorded',
                      body: isApiError(err) ? err.message : undefined,
                      error: err,
                    }),
                  )
              }
            >
              Check and record
            </Button>
          </>
        )
      }
    >
      <div className={styles.drawer}>
        {result ? (
          <>
            <p
              className={
                result.outcome === 'released' ? styles.okNote : styles.warnNote
              }
            >
              {result.message}
            </p>
            {result.mayRelease.length > 0 && (
              <p>
                May be released:{' '}
                <strong>
                  {result.mayRelease.map((t) => TOPIC_LABELS[t]).join(', ')}
                </strong>
              </p>
            )}
            <p className={styles.sub}>Logged against the patient.</p>
          </>
        ) : (
          <>
            <p className={styles.warnNote}>
              Check who is asking before sharing anything, by phone or at the
              counter. Every answer is logged against the patient.
            </p>
            <div className={styles.section}>
              <p className="eyebrow">Who is asking</p>
              <ChipToggle
                label="Who is asking"
                single
                options={keys(REQUESTER_LABELS).map((k) => ({
                  value: k,
                  label: REQUESTER_LABELS[k],
                }))}
                selected={[requester]}
                onChange={([v]) => setRequester(v)}
              />
            </div>
            <PatientPicker
              label="About"
              value={patient}
              onChange={setPatient}
            />
            <div className={styles.section}>
              <p className="eyebrow">What they want</p>
              <ChipToggle
                label="What they want"
                single
                options={keys(TOPIC_LABELS).map((k) => ({
                  value: k,
                  label: TOPIC_LABELS[k],
                }))}
                selected={[topic]}
                onChange={([v]) => setTopic(v)}
              />
            </div>
            <div className={styles.section}>
              <p className="eyebrow">How identity was checked</p>
              <ChipToggle
                label="How identity was checked"
                single
                options={keys(CHECK_LABELS).map((k) => ({
                  value: k,
                  label: CHECK_LABELS[k],
                }))}
                selected={[check]}
                onChange={([v]) => setCheck(v)}
              />
            </div>
            <Field label="Their name and phone (optional)">
              <Input
                value={name}
                placeholder="e.g. Priya Kumar, 012-655 1920"
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
          </>
        )}
      </div>
    </Dialog>
  );
}
